/**
 * W4 plan downgrade (Team → Pro at the end of the term) and L57 term downgrade (yearly →
 * monthly at the end of the term): pure presentation helpers for the review panel, the
 * pending-change banner, and the admin subscription page. The server (startups3
 * `SubscriptionScheduledChange`) owns eligibility; these only describe it.
 */
import {
  MEMBERSHIP_PRODUCT_RANK,
  MEMBERSHIP_TERM_MONTHS,
} from '@src/components/sco/UpgradeForm/membershipLadder'
import type { IconType } from 'react-icons'

import { MEMBERSHIP_TERM_IDS, membershipTermReadable } from './offers'
import { PLAN_FEATURES, PLAN_PRODUCT_IDS, planTierFor } from './planFeatures'
import { formatCents } from './presentation'

/** Chargebee states where cancel already wins over any plan change (matches the server). */
const CANCEL_PENDING_STATES = new Set(['non_renewing', 'cancelled', 'canceled'])

const SHORT_PLAN_NAMES: Record<string, string> = {
  [PLAN_PRODUCT_IDS.pro]: 'Pro',
  [PLAN_PRODUCT_IDS.team]: 'Team',
}

/** "Pro" / "Team" for the ladder plans, otherwise the caller's fallback name. */
export function planShortName(productId?: string | null, fallback?: string | null): string {
  return (productId && SHORT_PLAN_NAMES[productId]) || fallback?.trim() || 'your plan'
}

/** The Plans CTA for a move down that applies at renewal. */
export function switchAtRenewalLabel(productId?: string | null, fallback?: string | null): string {
  return `Switch to ${planShortName(productId, fallback)} at renewal`
}

/** The banner action that removes the scheduled change. */
export function keepPlanLabel(productId?: string | null, fallback?: string | null): string {
  return `Keep ${planShortName(productId, fallback)}`
}

/** The action that removes a scheduled term change (same plan, shorter term). */
export const KEEP_CURRENT_PLAN_LABEL = 'Keep current plan'

/**
 * L57 disclosure for a term downgrade: "Your plan switches to monthly on Nov 3, 2026. Nothing is
 * charged or credited now." The change is scheduled with no proration, so no credit exists.
 */
export function termChangeDisclosure(termId?: string | null, changeAt?: string | Date | null): string {
  const term = termId ? membershipTermReadable(termId) : 'its new billing period'
  const date = formatPlanChangeDate(changeAt ?? null)
  return `Your plan switches to ${term} ${date ? `on ${date}` : 'at your renewal'}. Nothing is charged or credited now.`
}

/** `Nov 3, 2026`. Null for a missing or unparseable date, so copy can drop the clause. */
export function formatPlanChangeDate(value?: string | Date | null): string | null {
  if (!value) {
    return null
  }
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date)
}

export type PlanChangeLine = {
  slug: string
  icon: IconType
  /** What the member has today. */
  title: string
  /** The same line after the change, when it is reduced rather than removed ("3 / day"). */
  after: string | null
  /** Today's amount when the line is reduced ("15 / day"). */
  before: string | null
}

/**
 * What ends or shrinks at the change, from the plan catalog (`planFeatures.ts`), so the review
 * never claims a loss the paywall does not sell. Team → Pro: Team DMs, office hours, premium
 * events, and investor messages 15 → 3 a day.
 */
export function planChangeEndingLines(
  fromProductId?: string | null,
  toProductId?: string | null
): PlanChangeLine[] {
  const fromTier = planTierFor(fromProductId)
  const toTier = planTierFor(toProductId)
  if (!fromTier || !toTier || fromTier === toTier) {
    return []
  }
  return PLAN_FEATURES.flatMap(feature => {
    const before = feature.plans[fromTier]
    if (!before) {
      return []
    }
    const after = feature.plans[toTier]
    if (after && after.title === before.title) {
      return []
    }
    if (after) {
      return [
        {
          slug: feature.slug,
          icon: feature.icon,
          title: feature.compareTitle || before.title,
          before: before.value || before.title,
          after: after.value || after.title,
        },
      ]
    }
    return [{ slug: feature.slug, icon: feature.icon, title: before.title, before: null, after: null }]
  })
}

type ProductLike = {
  productId?: string | null
  name?: string | null
  paywallName?: string | null
  title?: string | null
  termId?: string | null
  effectiveTermId?: string | null
  effectiveTerm?: { abbreviation?: string | null; name?: string | null } | null
}

export type PendingChangeSubscriptionLike = {
  subscriptionId?: string | null
  productId?: string | null
  isActive?: boolean | null
  hasPendingChange?: boolean | null
  pendingProductId?: string | null
  pendingTermId?: string | null
  pendingChangeAt?: string | null
  pendingProduct?: ProductLike | null
  product?: ProductLike | null
}

export type PendingPlanChange = {
  subscriptionId: string
  currentProductId: string | null
  currentPlanName: string
  pendingProductId: string | null
  pendingPlanName: string
  changeAt: string | null
  /** Billing term today and after the change (L57). Null when the row does not say. */
  currentTermId: string | null
  pendingTermId: string | null
  /** Same plan, shorter billing term: the banner names the term and offers "Keep current plan". */
  termOnly: boolean
}

function planName(product?: ProductLike | null, productId?: string | null): string {
  return (
    product?.name?.trim() ||
    product?.paywallName?.trim() ||
    product?.title?.trim() ||
    planShortName(productId || product?.productId, null)
  )
}

/** A subscription's scheduled change, normalized for the banner. Null when none is stored. */
export function pendingPlanChangeFor(
  subscription?: PendingChangeSubscriptionLike | null
): PendingPlanChange | null {
  if (!subscription?.subscriptionId) {
    return null
  }
  if (!subscription.hasPendingChange && !subscription.pendingProductId && !subscription.pendingChangeAt) {
    return null
  }
  const currentProductId = subscription.productId || subscription.product?.productId || null
  const pendingProductId = subscription.pendingProductId || subscription.pendingProduct?.productId || null
  const currentTermId = subscription.product?.effectiveTermId || subscription.product?.termId || null
  const pendingTermId =
    subscription.pendingTermId ||
    subscription.pendingProduct?.effectiveTermId ||
    subscription.pendingProduct?.termId ||
    null
  const samePlan = !pendingProductId || pendingProductId === currentProductId
  return {
    subscriptionId: subscription.subscriptionId,
    currentProductId,
    currentPlanName: planName(subscription.product, currentProductId),
    pendingProductId,
    pendingPlanName: planName(subscription.pendingProduct, pendingProductId || currentProductId),
    changeAt: subscription.pendingChangeAt || null,
    currentTermId,
    pendingTermId,
    termOnly: samePlan && Boolean(pendingTermId) && pendingTermId !== currentTermId,
  }
}

/** The first active subscription with a scheduled change. */
export function pendingPlanChange(
  subscriptions?: Array<PendingChangeSubscriptionLike | null> | null
): PendingPlanChange | null {
  for (const subscription of subscriptions || []) {
    if (!subscription?.isActive) {
      continue
    }
    const pending = pendingPlanChangeFor(subscription)
    if (pending) {
      return pending
    }
  }
  return null
}

/** True when the membership is already set to cancel: cancel wins, so no switch is offered. */
export function subscriptionIsCancelling(
  subscription?: {
    state?: string | null
    delayedCancelAt?: string | null
  } | null
): boolean {
  return Boolean(subscription?.delayedCancelAt || CANCEL_PENDING_STATES.has(subscription?.state || ''))
}

/** "Switching to Startups Pro on Nov 3, 2026", or for a term change "Switching to monthly on …". */
export function pendingPlanChangeHeadline(
  pending: Pick<PendingPlanChange, 'pendingPlanName' | 'changeAt'> &
    Partial<Pick<PendingPlanChange, 'termOnly' | 'pendingTermId'>>
) {
  const date = formatPlanChangeDate(pending.changeAt)
  const target =
    pending.termOnly && pending.pendingTermId
      ? `${membershipTermReadable(pending.pendingTermId)} billing`
      : pending.pendingPlanName
  return date ? `Switching to ${target} on ${date}` : `Switching to ${target} at renewal`
}

/** The banner body: what the member keeps until the change and what it renews as. */
export function pendingPlanChangeDetail(pending: PendingPlanChange): string {
  const date = formatPlanChangeDate(pending.changeAt)
  const until = date ? `until ${date}` : 'until your renewal'
  if (pending.termOnly && pending.pendingTermId) {
    const current = pending.currentTermId
      ? ` on ${membershipTermReadable(pending.currentTermId)} billing`
      : ''
    return `You keep ${pending.currentPlanName}${current} ${until}. It then renews ${membershipTermReadable(
      pending.pendingTermId
    )}. Nothing is charged or credited now.`
  }
  return `You keep ${pending.currentPlanName} ${until}. Your plan then renews as ${pending.pendingPlanName}.`
}

/** "Keep Team" for a plan change, "Keep current plan" for a term change. */
export function pendingKeepLabel(pending: PendingPlanChange): string {
  return pending.termOnly
    ? KEEP_CURRENT_PLAN_LABEL
    : keepPlanLabel(pending.currentProductId, pending.currentPlanName)
}

/**
 * The renewal price after the change, from the quote's next invoice estimate (coupons applied)
 * with the target's own billing period. Falls back to the catalog price when Chargebee omits it.
 */
export function renewalPriceLabel(
  nextBillingAmountInCents?: number | null,
  product?: ProductLike | null,
  fallback?: string | null
): string | null {
  if (nextBillingAmountInCents == null) {
    return fallback || null
  }
  const abbreviation = product?.effectiveTerm?.abbreviation?.trim()
  const termId = product?.effectiveTermId || product?.termId
  const suffix = abbreviation ? `/${abbreviation}` : termId === 'month' ? '/mo' : ''
  return `${formatCents(nextBillingAmountInCents)}${suffix}`
}

/** Human list of coupon ids that stop at the change ("SAVE50 and TEAM20"). */
export function droppedCouponsLabel(couponIds?: Array<string | null> | null): string | null {
  const ids = (couponIds || []).filter((id): id is string => Boolean(id?.trim())).map(id => id.trim())
  if (ids.length === 0) {
    return null
  }
  if (ids.length === 1) {
    return ids[0]
  }
  return `${ids.slice(0, -1).join(', ')} and ${ids[ids.length - 1]}`
}

export type PlanChangeLogLike = {
  action?: string | null
  description?: string | null
  dataAsJson?: string | null
}

const LOG_ACTION_LABELS: Record<string, string> = {
  plan_change_scheduled: 'Scheduled',
  plan_change_unscheduled: 'Scheduled change removed',
  plan_change_applied_now: 'Applied now',
}

/** Admin history row: a readable action, the from → to products, and any stated reason. */
export function describePlanChangeLog(log: PlanChangeLogLike) {
  let data: Record<string, unknown> = {}
  if (log.dataAsJson) {
    try {
      const parsed = JSON.parse(log.dataAsJson)
      data = parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      data = {}
    }
  }
  const text = (key: string) => (typeof data[key] === 'string' && (data[key] as string).trim()) || null
  const from = text('fromProductId') || text('pendingProductId')
  const to = text('toProductId')
  const term = text('toTermId') || text('pendingTermId')
  const change = from && to ? `${from} → ${to}${term ? ` (${term})` : ''}` : to || from || null
  return {
    label: (log.action && LOG_ACTION_LABELS[log.action]) || log.action || 'Change',
    change,
    reason: log.description?.trim() || null,
    byAdmin: data.isAdminAction === true,
  }
}

const MEMBERSHIP_PRODUCT_NAMES: Record<string, string> = {
  'scom-accelerator-basic': 'Basic',
  'scom-accelerator-tools': 'Startups Pro',
  'scom-accelerator-community': 'Community',
  'scom-accelerator-advisor': 'Advisor Team',
}

/**
 * Admin picker: ladder plans ranked below the current one, highest first (the only moves the
 * server schedules or applies now). Empty off the ladder or on the lowest plan.
 */
export function lowerMembershipProducts(currentProductId?: string | null) {
  const currentRank = currentProductId ? MEMBERSHIP_PRODUCT_RANK[currentProductId] : undefined
  if (currentRank == null) {
    return []
  }
  return Object.entries(MEMBERSHIP_PRODUCT_RANK)
    .filter(([, rank]) => rank < currentRank)
    .sort(([leftId, left], [rightId, right]) => right - left || leftId.localeCompare(rightId))
    .map(([productId]) => ({ productId, label: MEMBERSHIP_PRODUCT_NAMES[productId] || productId }))
}

/** L57: membership terms shorter than `currentTermId`, shortest first. Empty when unknown. */
export function shorterMembershipTermIds(currentTermId?: string | null): string[] {
  const current = currentTermId ? MEMBERSHIP_TERM_MONTHS[currentTermId] : undefined
  if (current == null) {
    return []
  }
  return MEMBERSHIP_TERM_IDS.filter(termId => MEMBERSHIP_TERM_MONTHS[termId] < current)
}

export type AdminPlanChangeProduct = {
  productId: string
  label: string
  /** The current plan, offered only for a shorter billing term (L57 term downgrade). */
  termOnly?: boolean
}

/**
 * Admin picker targets, matching what the server schedules or applies now: ladder plans below
 * the current one (`lowerMembershipProducts`), plus the current plan when a shorter billing term
 * exists (L57). Empty off the ladder, or on the lowest plan with the shortest term.
 */
export function adminPlanChangeProducts(
  currentProductId?: string | null,
  currentTermId?: string | null
): AdminPlanChangeProduct[] {
  const lower: AdminPlanChangeProduct[] = lowerMembershipProducts(currentProductId)
  if (
    !currentProductId ||
    MEMBERSHIP_PRODUCT_RANK[currentProductId] == null ||
    shorterMembershipTermIds(currentTermId).length === 0
  ) {
    return lower
  }
  const name = MEMBERSHIP_PRODUCT_NAMES[currentProductId] || currentProductId
  return [...lower, { productId: currentProductId, label: `${name}, shorter term`, termOnly: true }]
}
